import { Divider, Grid, Typography, withWidth } from "@material-ui/core";
import { CommonValueOutput } from "common/components";
import { InvestmentField, InvestmentInputTypeKey } from "common/components/input/fields";
import { SubformProps } from "common/components/SubformProps";
import { CreateInvestmentManageable, InvestmentManageable } from "common/store/investment/types";
import { SaleManageable } from "common/store/sale/types";
import { isWidth, ScreenWidth } from "common/utils/width";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface ChooseAmountSubformProps extends SubformProps<CreateInvestmentManageable> {
  sale: SaleManageable;
  width: ScreenWidth;
  currentInvestmentAmount: number;
}

const ChooseAmountSubform = (props: ChooseAmountSubformProps) => {
  const {
    values,
    setFieldValue,
    sale,
    width,
    currentInvestmentAmount,
  } = props;

  // Hooks
  const { t } = useTranslation();

  useEffect(
    () => {
      if (values.minAmount !== undefined && values.amountShares < values.minAmount) {
        setFieldValue(
          "amountShares",
          values.minAmount
        );
      } else if (values.maxAmount !== undefined && values.amountShares > values.maxAmount) {
        setFieldValue(
          "amountShares",
          values.maxAmount
        );
      }
    },
    [values.minAmount, values.maxAmount, values.amountShares, setFieldValue]
  );

  const isLowSoftLimitReached = useMemo(
    () => currentInvestmentAmount + values.amountShares > InvestmentManageable.getSelfDisclosureInfo().availableCashDisclosureLimit,
    [currentInvestmentAmount, values.amountShares, InvestmentManageable.getSelfDisclosureInfo()]
  );
  const isHighSoftLimitReached = useMemo(
    () => currentInvestmentAmount + values.amountShares > InvestmentManageable.getSelfDisclosureInfo().monthlyNetSalaryDisclosureLimit,
    [currentInvestmentAmount, values.amountShares, InvestmentManageable.getSelfDisclosureInfo()]
  );
  const showSelfDisclosure = useMemo(
    () => isLowSoftLimitReached && sale.isSelfDisclosureRequired(),
    [isLowSoftLimitReached, sale.isSelfDisclosureRequired()]
  );

  return (
    <Grid container direction="column" spacing={6}>
      <Grid container direction="row" item spacing={isWidth(
        "xs",
        width
      ) ? 4 : 8}>
        <Grid item sm={6} xs={12}>
          <CommonValueOutput
            label={t("Tokens")}
            value={`${values.amountShares}`}
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <CommonValueOutput
            label={t("Remaining tokens")}
            value={`${sale.sharesAvailable - values.amountShares}/${sale.platformAssetValue}`}
          />
        </Grid>
      </Grid>

      <Grid item container direction="column" spacing={4}>
        <Grid item xs={12}>
          <InvestmentField
            id={"amountShares" as InvestmentInputTypeKey}
            {...props}
          />
        </Grid>

        <Grid item xs={12}>
          <InvestmentField
            id={"amountShares" as InvestmentInputTypeKey}
            variant="slider"
            {...props}
          />
        </Grid>
      </Grid>

      {sale.isSelfDisclosureRequired() &&
      <Grid item xs={12}>
        <Typography variant="body1" color="textSecondary">
          {t("Due to legal requirements, the maximum investment amount per person for this capital market product is 25.000,- Euro.")}
        </Typography>
      </Grid>
      }

      <Grid item xs={12}>
        <Typography className="subtitle--big--inner" color="textSecondary">
          {t("Beneficial owner")}
        </Typography>

        <Divider/>

        <InvestmentField
          id={"investingForThemselves" as InvestmentInputTypeKey}
          {...props}
        />
      </Grid>

      {showSelfDisclosure &&
      <Grid item container direction="column" spacing={2}>
        <Grid item xs={12}>
          <Typography className="subtitle--big--inner" color="textSecondary">
            {t("Self-disclosure")}
          </Typography>

          <Divider/>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body1" color="textSecondary">
            {t("With this capital investment the amount of the subscription sum is limited and bound to certain conditions. In this case, the German Securities Prospectus Act requires us to obtain your self-disclosure.")}
          </Typography>
        </Grid>

        {isHighSoftLimitReached &&
        <Grid item xs={12}>
          <Typography variant="body1" color="textSecondary">
            {t("An investment of more than 10,000 euros is only possible if you can confirm the following statement")}
          </Typography>
        </Grid>
        ||
        isLowSoftLimitReached &&
        <>
          <Grid item xs={12}>
            <Typography variant="body1" color="textSecondary">
              {t("An investment between 1,000 and 10,000 euros is only possible if you can confirm at least one of the following two statements")}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <InvestmentField
              id={"selfDisclosedAvailableCash" as InvestmentInputTypeKey}
              {...props}
            />
          </Grid>
        </>
        }

        <Grid item xs={12}>
          <InvestmentField
            id={"selfDisclosedMonthlyNetSalary" as InvestmentInputTypeKey}
            {...props}
          />
        </Grid>
      </Grid>
      }
    </Grid>
  );
};

export default withWidth()(ChooseAmountSubform);
